import React, { useState } from 'react';
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import { Download as DownloadIcon, Upload as UploadIcon } from '@mui/icons-material';
import MatchDataService from '../services/MatchDataService';

const MatchDataPull = ({ onDataPulled }) => {
  const [matchId, setMatchId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  const showNotification = (message, severity = 'info') => {
    setNotification({
      open: true,
      message,
      severity,
    });
  };

  const pullMatchData = async () => {
    if (!matchId.trim()) {
      setError('Please enter a match ID');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const matchData = await MatchDataService.pullMatchData(matchId.trim());

      // Parse the data and call the callback
      const parsedData = MatchDataService.parseMatchDataToPicksFormat(matchData);

      if (onDataPulled) {
        onDataPulled(parsedData);
      }

      showNotification('Match data pulled successfully!', 'success');
      // Keep the match ID in the input box
      setIsLoading(false);

    } catch (err) {
      setIsLoading(false);
      console.error('Error pulling match data:', err);

      // No more manual process dialog - just show error
      setError(`Failed to pull match data: ${err.message}`);
      showNotification(`Failed to pull match data: ${err.message}`, 'error');
    }
  };

  const loadMatchDataFile = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const matchData = await MatchDataService.loadMatchDataFile();
      const parsedData = MatchDataService.parseMatchDataToPicksFormat(matchData);
      
      if (onDataPulled) {
        onDataPulled(parsedData);
      }

      showNotification('Match data loaded successfully!', 'success');
      setIsLoading(false);
      
    } catch (err) {
      setIsLoading(false);
      console.error('Error loading match data file:', err);
      setError(`Failed to load match data: ${err.message}`);
      showNotification(`Failed to load match data: ${err.message}`, 'error');
    }
  };


  return (
    <>
      <Paper
        elevation={3}
        sx={{
          padding: { xs: 1.5, sm: 2 },
          marginBottom: { xs: 2, sm: 3 },
          backgroundColor: '#1e1e1e',
          border: '1px solid #333'
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: '#fff',
            marginBottom: { xs: 1.5, sm: 2 },
            fontSize: { xs: '1rem', sm: '1.25rem' }
          }}
        >
          Pull Match Data
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: { xs: 1, sm: 2 },
            alignItems: 'stretch',
            marginBottom: { xs: 1, sm: 2 },
            flexDirection: { xs: 'column', sm: 'row' }
          }}
        >
          <TextField
            label="Match ID"
            variant="outlined"
            value={matchId}
            onChange={(e) => setMatchId(e.target.value)}
            placeholder="e.g., 8449477805"
            disabled={isLoading}
            size="small"
            sx={{
              flex: { xs: '1 1 auto', sm: 1 },
              width: { xs: '100%', sm: 'auto' },
              '& .MuiInputBase-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: 'white',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.7)',
              },
            }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={pullMatchData}
            disabled={isLoading || !matchId.trim()}
            startIcon={isLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minWidth: { xs: '100%', sm: 140 },
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}
          >
            {isLoading ? 'Loading...' : 'Pull Data'}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ marginTop: { xs: 1, sm: 2 } }}>
            {error}
          </Alert>
        )}
      </Paper>


      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      >
        <Alert
          severity={notification.severity}
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MatchDataPull;